import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Chip
} from '@mui/material';
import { useAuth } from '../AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Agriculture as AgricultureIcon,
  Logout as LogoutIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Person as PersonIcon,
  Article as ArticleIcon,
  Assignment as AssignmentIcon,
  Email as EmailIcon,
  Storefront as StorefrontIcon // Ajout de l'icône pour les annonces
} from '@mui/icons-material';
import NotificationsComponent from '../NotificationsComponent';

/**
 * Composant de barre supérieure pour le tableau de bord administrateur en version mobile
 */
const TopBar = ({ activeSection, setActiveSection, handleLogout }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [userData, setUserData] = useState(null);
  const { currentUser } = useAuth();
  const open = Boolean(anchorEl);

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des données utilisateur:', error);
        }
      }
    };

    fetchUserData();
  }, [currentUser]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (section) => {
    setActiveSection(section);
    handleClose();
  };

  const handleLogoutClick = () => {
    handleClose();
    handleLogout();
  };

  return (
    <AppBar position="fixed" sx={{ bgcolor: '#014527', display: 'block', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        <IconButton
          size="large"
          edge="start"
          color="inherit"
          aria-label="menu"
          sx={{ mr: 2 }}
          onClick={handleClick}
        >
          <MenuIcon />
        </IconButton>
        
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Admin AVG
        </Typography>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          justifyContent: 'flex-end', // Aligner à droite
          ml: 'auto' // Pousser vers la droite
        }}>
          <NotificationsComponent />
          
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row', // Aligner horizontalement
              alignItems: 'center', // Centrer verticalement
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
            onClick={handleClick}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', // Empiler verticalement le nom et le poste
              alignItems: 'flex-end', // Aligner à droite
              mr: 1, // Marge à droite pour séparer du avatar
              maxWidth: 120, // Limite la largeur du bloc
              overflow: 'hidden' // Cache le débordement
            }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'white',
                  fontWeight: 'medium',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 120 // Limite la largeur du texte
                }}
              >
                {userData?.name || 'Administrateur'}
              </Typography>
              {userData?.position && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'white',
                    opacity: 0.9,
                    fontStyle: 'italic',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 120 // Limite la largeur du texte
                  }}
                >
                  {userData.position}
                </Typography>
              )}
            </Box>
            <Avatar
              sx={{
                bgcolor: 'white',
                color: '#014527',
                width: 32,
                height: 32
              }}
            >
              <PersonIcon />
            </Avatar>
          </Box>
        </Box>
        
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItem 
            onClick={() => handleMenuItemClick('dashboard')}
            selected={activeSection === 'dashboard'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DashboardIcon sx={{ mr: 1 }} />
              Tableau de bord
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => handleMenuItemClick('admin-requests')}
            selected={activeSection === 'admin-requests'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AdminIcon sx={{ mr: 1 }} />
              Demandes d'administrateurs
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => handleMenuItemClick('administrators')}
            selected={activeSection === 'administrators'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SupervisorAccountIcon sx={{ mr: 1 }} />
              Administrateurs
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => handleMenuItemClick('farmers')}
            selected={activeSection === 'farmers'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PeopleIcon sx={{ mr: 1 }} />
              Agriculteurs
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => handleMenuItemClick('announcements')}
            selected={activeSection === 'announcements'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StorefrontIcon sx={{ mr: 1 }} />
              Annonces
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => handleMenuItemClick('diagnostics')}
            selected={activeSection === 'diagnostics'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AssignmentIcon sx={{ mr: 1 }} />
              Diagnostics
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => handleMenuItemClick('newsletter')}
            selected={activeSection === 'newsletter'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EmailIcon sx={{ mr: 1 }} />
              Newsletter
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => handleMenuItemClick('projects')}
            selected={activeSection === 'projects'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BarChartIcon sx={{ mr: 1 }} />
              Projets
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => handleMenuItemClick('statistics')}
            selected={activeSection === 'statistics'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BarChartIcon sx={{ mr: 1 }} />
              Statistiques
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => handleMenuItemClick('news')}
            selected={activeSection === 'news'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ArticleIcon sx={{ mr: 1 }} />
              Actualités
            </Box>
          </MenuItem>
          <MenuItem 
            onClick={() => handleMenuItemClick('settings')}
            selected={activeSection === 'settings'}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SettingsIcon sx={{ mr: 1 }} />
              Paramètres
            </Box>
          </MenuItem>
          <MenuItem onClick={handleLogoutClick}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LogoutIcon sx={{ mr: 1 }} />
              Déconnexion
            </Box>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
